import type { Reference } from '@medplum/fhirtypes'
import {
  CALENDAR_BLOCK_TEXT,
  CALENDAR_COLORS,
  type CalendarColor,
  UNASSIGNED_CALENDAR_COLOR,
} from '#src/calendarColors.ts'
import type { ExtendedAppointment } from '#src/models/appointments/appointment.ts'
import { getReferenceString } from '#src/references.ts'

// Indexed calendar-event coloring. A block's color comes from the calendar it
// sits on (a practitioner, participant, location, or service), never from the
// appointment type. See the calendar page for how the paint is applied.

const PALETTE = CALENDAR_COLORS

function toKey(reference: Reference | string): string {
  return typeof reference === 'string'
    ? reference
    : getReferenceString(reference)
}

// Assigns palette colors to references by their position in an ordered list, so
// distinct references get distinct colors until the palette is exhausted (then
// it wraps). Sequential assignment — not per-reference hashing — is what avoids
// two adjacent filters colliding on the same color. Build one map per calendar
// render from the active filter list followed by any other visible
// practitioners, and share it between the event paint and the filter chips.
export class AppointmentColorMap {
  private readonly colors: Map<string, CalendarColor>

  constructor(orderedRefs: (Reference | string)[]) {
    this.colors = new Map()
    let next = 0
    for (const ref of orderedRefs) {
      const key = toKey(ref)
      if (!this.colors.has(key)) {
        this.colors.set(key, PALETTE[next % PALETTE.length])
        next++
      }
    }
  }

  colorFor(reference: Reference | string): CalendarColor {
    // References outside the ordered list fall back to the first palette color;
    // callers should include every reference they render up front.
    return this.colors.get(toKey(reference)) ?? PALETTE[0]
  }
}

export type CalendarBlockTreatment = 'full' | 'outline'

export interface CalendarBlockPaint {
  background: string
  borderColor: string
  color: string
}

// Full blocks are the 100 fill with dark text. Outline blocks are a white
// fill, an 800 border, and 900 text, used when a Google invite is not a firm
// busy.
export function calendarBlockPaint(
  color: CalendarColor,
  treatment: CalendarBlockTreatment
): CalendarBlockPaint {
  if (treatment === 'outline') {
    return {
      background: '#ffffff',
      borderColor: color.border,
      color: color.text,
    }
  }
  return {
    background: color.fill,
    borderColor: color.fill,
    color: CALENDAR_BLOCK_TEXT,
  }
}

// Which calendar a single block should take its color from.
//
// A selected practitioner calendar wins, then any other selected calendar the
// appointment is on, then the appointment's own practitioner. One appointment
// keeps one block; it does not split into a card per calendar.
export function calendarOwnerRef(
  participantRefs: readonly string[],
  activeFilterRefs: readonly string[]
): string | undefined {
  const participants = new Set(participantRefs)
  const matchedPractitioner = activeFilterRefs.find(
    (ref) => participants.has(ref) && ref.startsWith('Practitioner/')
  )
  if (matchedPractitioner) {
    return matchedPractitioner
  }
  const matched = activeFilterRefs.find((ref) => participants.has(ref))
  if (matched) {
    return matched
  }
  // A practitioner on the appointment, even when their calendar is not
  // selected. A patient or service is not an owner; the caller paints the
  // neutral color instead of borrowing the first palette slot.
  return participantRefs.find((ref) => ref.startsWith('Practitioner/'))
}

export function calendarColorForAppointment({
  participantRefs,
  activeFilterRefs,
  colorMap,
}: {
  participantRefs: readonly string[]
  activeFilterRefs: readonly string[]
  colorMap: AppointmentColorMap
}): CalendarColor {
  const owner = calendarOwnerRef(participantRefs, activeFilterRefs)
  return owner ? colorMap.colorFor(owner) : UNASSIGNED_CALENDAR_COLOR
}

export interface AppointmentEventColors {
  // One color per active filter the event matches. Empty when no filters match.
  bands: CalendarColor[]
}

// With active filters, a band per filter reference the appointment matches.
// The calendar paints a single block from calendarColorForAppointment; these
// bands remain for callers that want every matching calendar's color.
export function appointmentEventColors({
  appointment,
  activeFilterRefs,
  colorMap,
}: {
  appointment: ExtendedAppointment
  activeFilterRefs: string[]
  colorMap: AppointmentColorMap
}): AppointmentEventColors {
  if (activeFilterRefs.length === 0) {
    return { bands: [] }
  }

  const participantRefs = new Set(appointment.participantReferenceStrings)
  const bands = activeFilterRefs
    .filter((ref) => participantRefs.has(ref))
    .map((ref) => colorMap.colorFor(ref))
  return { bands }
}

// The event fill: the profile color itself for one band, or a hard-stop
// linear gradient splitting the block evenly across all matching calendars.
// The fills are already the light 100 colors, so they are not tinted again.
export function bandsBackground(bands: string[]): string {
  if (bands.length === 1) {
    return bands[0]
  }
  const step = 100 / bands.length
  const stops = bands
    .map((color, i) => `${color} ${i * step}% ${(i + 1) * step}%`)
    .join(', ')
  return `linear-gradient(135deg, ${stops})`
}
